import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import pkg from "../../package.json";
import {
  ArrowRight,
  Envelope,
  ChartLineUp,
  Gear,
  EnvelopeSimple,
  SignOut,
  Bell,
  Cake,
  Trophy,
  Heart,
  UsersThree,
  CalendarBlank,
  Sparkle,
  ShieldCheck,
  Buildings,
  ClipboardText,
} from "@phosphor-icons/react";
// CrewMessageBanner import retired 2026-02-27 alongside the on-home
// message banner. The component still lives in DashboardFeatures.js
// as dead code — safe to purge later if we don't revive it.
// import { CrewMessageBanner } from "./DashboardFeatures";
import YourTasksTodayCard from "../components/YourTasksTodayCard";
import TilePersonality from "../components/TilePersonality";
import DailyPasscodeCard from "../components/DailyPasscodeCard";
import { routeNotification } from "../lib/notificationRouter";
import HomeTopCards from "../components/HomeTopCards";
import CouponHeaderPill from "../components/CouponHeaderPill";
import { NotificationsPanel } from "./ManagerDashboard";
import EODReportModal from "../components/EODReportModal";
import InstallAppPrompt from "../components/InstallAppPrompt";
import PushNudge from "../components/PushNudge";
import PushHardGate from "../components/PushHardGate";
import WalkieFAB from "../components/WalkieFAB";
import CreditByDom from "../components/CreditByDom";
import StoriesRow from "../components/StoriesRow";
// InstantsFab import removed 2026-06-15 with Instants retirement.
import May4Crawl from "../components/May4Crawl";
import PatrioticFireworks from "../components/PatrioticFireworks";
import GoldGlitter from "../components/GoldGlitter";
import GoldieDropToast from "../components/GoldieDropToast";
import useGoldieStage from "../hooks/useGoldieStage";
import formatLastLogin from "../utils/formatLastLogin";
import Banner from "../components/Banner";
import tap from "../utils/haptics";
import { playTap } from "../lib/sounds";
// LiveTicker import removed 2026-05-28 — component still lives in
// /app/frontend/src/components/LiveTicker.js in case we want to bring
// back the rotating pill later.
// import LiveTicker from "../components/LiveTicker";
import MilestoneCelebration from "../components/MilestoneCelebration";
import useScreenshotDetect from "../hooks/useScreenshotDetect";
import ReportIssueModal from "../components/ReportIssueModal";
import { useTheme } from "../ThemeContext";
import useNotificationCue from "../hooks/useNotificationCue";
import { SpeakerHigh, SpeakerSlash, ChatTeardropDots, X } from "@phosphor-icons/react";
import WhatsNewLink from "../components/WhatsNewLink";  // eslint-disable-line no-unused-vars -- retained for Reports changelog reuse

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Profile avatar with a small "+" badge that opens a popover menu:
// "Post a story" (when enabled) and "Edit profile". Keeps the home header
// clean — the old "Share a quick story" pill is gone and everything lives on
// the avatar. Click-outside closes the menu.
export function ProfileMenuButton({ user, myPhoto, storiesEnabled, onEditProfile, onPostStory, onAdmin, onSettings, onSignOut, onOpenReports, onOpenKeepsake, isAdmin = false, onOpenNotifications, unreadCount = 0, cueMuted = false, onToggleCueMuted }) {
  const [open, setOpen] = useState(false);
  // Celebration keepsakes — the user's own past birthday/anniversary cards
  // (moved here 2026-06-21 from the Edit Profile screen: "these should be
  // celebrated — move them to the profile slide-out"). Refetched each time
  // the panel opens so freshly-signed belated cards show up.
  const [keepsakes, setKeepsakes] = useState([]);
  useEffect(() => {
    if (!open || !user?.crew_id) return undefined;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/birthday/keepsakes?actor_id=${encodeURIComponent(user.crew_id)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setKeepsakes(d?.keepsakes || []); })
      .catch(() => { if (!cancelled) setKeepsakes([]); });
    return () => { cancelled = true; };
  }, [open, user?.crew_id]);
  // Reports "new since your last visit" signal. Replaces the old weekly
  // heuristic with a real backend count (attendance events, open bug
  // reports, push delivery errors, changelog entries). When lit, the
  // Reports row shows a pulsing rose dot AND lists what specifically
  // needs review so the user isn't guessing. (Fix 2026-02-27 — user:
  // "red dot lights but doesn't show what review is needed".)
  const [reportsNew, setReportsNew] = useState(null);
  useEffect(() => {
    if (!onOpenReports || !user?.crew_id) return undefined;
    let cancelled = false;
    const load = () => {
      try {
        const key = `wowReportsHomeDotSeen:${user.crew_id}`;
        // Empty / null → epoch so a fresh install still surfaces the
        // real backlog instead of the backend's 7-day fallback window.
        const since = localStorage.getItem(key) || "1970-01-01T00:00:00Z";
        // Use axios (already imported via the file). This module doesn't
        // import axios directly; the fetch keeps the dependency footprint
        // untouched. Errors fall through to null → no dot.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/new-since?actor_id=${encodeURIComponent(user.crew_id)}&since=${encodeURIComponent(since)}`, {
          credentials: "include",
        })
          .then((r) => r.ok ? r.json() : null)
          .then((data) => { if (!cancelled) setReportsNew(data || null); })
          .catch(() => { if (!cancelled) setReportsNew(null); });
      } catch { if (!cancelled) setReportsNew(null); }
    };
    load();
    // Refresh whenever the menu opens so the count is fresh.
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onOpenReports
    // is recreated by App.js on every render; depending on it fires this
    // effect 9× per session. crew_id + open is enough.
  }, [user?.crew_id, open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the side panel is open so the page doesn't
    // jiggle beneath it on iOS.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);
  const close = () => setOpen(false);
  const initial = (user?.first_name || "?").charAt(0).toUpperCase();
  // Slide-out side panel (2026-05-25 user ask: "liked the slide out idea
  // when you click on your profile picture rather than the drop down").
  // Renders OVER the page with a tinted scrim. All the user-scoped
  // actions live here so the header stays uncluttered.
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative w-9 h-9 rounded-full overflow-visible transition-all active:scale-95"
        data-testid="header-profile-button"
        aria-label="Profile menu"
        aria-expanded={open}
        title="Profile menu"
      >
        <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white">
          {myPhoto ? (
            <img src={myPhoto} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-white font-black text-sm"
              style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
            >
              {initial}
            </div>
          )}
        </div>
        {storiesEnabled && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-blue-600 border-2 border-white flex items-center justify-center shadow"
            aria-hidden
          >
            <span className="text-white font-black text-[10px] leading-none">+</span>
          </span>
        )}
        {/* Unread-count badge — shows when Notifications are folded into
            this menu (V2 home). Top-right of the avatar so it reads as a
            "you have mail" indicator without a separate bell button. */}
        {unreadCount > 0 && onOpenNotifications && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[9px] font-black text-white min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center shadow tabular-nums"
            style={{ backgroundColor: "var(--fb-blue)" }}
            data-testid="profile-panel-unread-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <>
          {/* Scrim */}
          <button
            type="button"
            aria-label="Close profile menu"
            onClick={close}
            className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm animate-[fadeIn_180ms_ease-out]"
            data-testid="profile-panel-scrim"
            style={{ animation: "fadeIn 180ms ease-out forwards" }}
          />
          {/* Side panel — frosted glass so the page peeks through. */}
          <aside
            className="fixed top-0 right-0 bottom-0 z-[81] w-[84%] max-w-xs shadow-2xl flex flex-col"
            role="dialog"
            aria-modal="true"
            data-testid="profile-panel"
            style={{
              animation: "slideInRight 220ms cubic-bezier(0.16, 1, 0.3, 1) forwards",
              background: "rgba(255, 255, 255, 0.92)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              borderLeft: "1px solid rgba(255, 255, 255, 0.55)",
            }}
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-blue-100 flex-shrink-0">
                {myPhoto ? (
                  <img src={myPhoto} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-white font-black text-xl"
                    style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                  >
                    {initial}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-base font-black text-slate-900 leading-tight truncate">
                  {user?.first_name || "Crew"}
                </p>
                {user?.position && (
                  <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">
                    {user.position}
                  </p>
                )}
              </div>
              <button
                onClick={close}
                className="ml-auto w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                data-testid="profile-panel-close"
                aria-label="Close"
              >
                <X weight="bold" className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            {/* Actions */}
            <nav className="flex-1 overflow-y-auto py-2">
              {/* Notifications — folded into the profile menu on V2 so the
                  header stays a single-icon "you" anchor. Only rendered
                  when the parent supplies the callback. */}
              {onOpenNotifications && (
                <PanelRow
                  testid="profile-panel-notifications"
                  emoji="🔔"
                  label={unreadCount > 0 ? `Notifications · ${unreadCount > 9 ? "9+" : unreadCount}` : "Notifications"}
                  dot={unreadCount > 0}
                  onClick={() => { close(); onOpenNotifications(); }}
                />
              )}
              {/* Sound toggle — inline row rather than a menu item because
                  it doesn't navigate. Uses the same emoji swap as the old
                  header cluster to match muscle memory. */}
              {onToggleCueMuted && (
                <button
                  type="button"
                  data-testid="profile-panel-sound"
                  onClick={() => onToggleCueMuted()}
                  className="w-full px-5 py-3 text-left text-sm font-bold flex items-center gap-3 transition-colors text-slate-700 hover:bg-slate-50"
                >
                  <span className="text-lg flex-shrink-0" style={{ width: "1.4rem", textAlign: "center" }}>{cueMuted ? "🔇" : "🔊"}</span>
                  <span className="flex-1">Sound {cueMuted ? "muted" : "on"}</span>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${cueMuted ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-800"}`}>
                    {cueMuted ? "Off" : "On"}
                  </span>
                </button>
              )}
              {storiesEnabled && (
                <PanelRow
                  testid="profile-panel-post-story"
                  emoji="📸"
                  label="Post a story"
                  onClick={() => { close(); onPostStory && onPostStory(); }}
                />
              )}
              <PanelRow
                testid="profile-panel-edit"
                emoji="👤"
                label="Edit profile"
                onClick={() => { close(); onEditProfile && onEditProfile(); }}
              />
              {onSettings && (
                <PanelRow
                  testid="profile-panel-settings"
                  emoji="⚙️"
                  label="Settings"
                  onClick={() => { close(); onSettings && onSettings(); }}
                />
              )}
              {/* Reports — 2026-02 moved off the Home grid into the
                  Profile slide-out. Only rendered for managers/admins/
                  DMs (parent passes onOpenReports iff isManager||isDM).
                  A tiny rose dot appears when the current MST-week
                  hasn't been opened yet — heuristic uses
                  localStorage.wowReportsLastSeen so no server call is
                  needed. Cleared the moment the user taps in. */}
              {onOpenReports && (() => {
                // Build a "why" sub-line from the /reports/new-since payload.
                // Only lit when there's actually something to review; each
                // pill hints at what the tap will surface.
                const buckets = [];
                if (reportsNew?.attendance) buckets.push(`${reportsNew.attendance} attendance`);
                if (reportsNew?.bug_reports) buckets.push(`${reportsNew.bug_reports} bug${reportsNew.bug_reports === 1 ? "" : "s"}`);
                if (reportsNew?.push_errors) buckets.push(`${reportsNew.push_errors} push issue${reportsNew.push_errors === 1 ? "" : "s"}`);
                if (reportsNew?.changelog) buckets.push(`${reportsNew.changelog} changelog`);
                const total = reportsNew?.total || 0;
                const dotOn = total > 0;
                return (
                  <PanelRow
                    testid="profile-panel-reports"
                    emoji="📊"
                    label={buckets.length > 0 ? `Reports · ${buckets.join(" · ")}` : "Reports"}
                    accent="rose"
                    dot={dotOn}
                    onClick={() => {
                      // Stamp a *Home-only* seen key so the dot doesn't
                      // re-light on next menu open. `wowReportsLastSeen`
                      // is intentionally left untouched — ReportsPage
                      // owns it so the NEW pill per tab reflects
                      // reality, not the moment the user clicked
                      // through. (Fix 2026-02-27 — testing agent iter66
                      // caught HomePage overwriting the key before
                      // ReportsPage could read it.)
                      try {
                        const key = `wowReportsHomeDotSeen:${user?.crew_id || "anon"}`;
                        localStorage.setItem(key, new Date().toISOString());
                      } catch { /* quota */ }
                      close();
                      onOpenReports && onOpenReports();
                    }}
                  />
                );
              })()}
              {isAdmin && onAdmin && (
                <PanelRow
                  testid="profile-panel-admin"
                  emoji="🛡️"
                  label="Admin"
                  accent="amber"
                  onClick={() => { close(); onAdmin && onAdmin(); }}
                />
              )}
            </nav>

            {/* Celebrations — the user's own past birthday/anniversary cards,
                moved here from Edit Profile (2026-06-21). Tapping opens the
                card so they can revisit it or Save it as an image. */}
            {onOpenKeepsake && keepsakes.length > 0 && (
              <div className="px-4 pt-3 pb-1" data-testid="profile-panel-keepsakes">
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  🎉 Your celebrations
                </p>
                <div className="space-y-2">
                  {keepsakes.map((k) => {
                    const dt = (() => {
                      try { return new Date(k.date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
                      catch { return k.date; }
                    })();
                    return (
                      <button
                        key={`${k.date}-${k.kind}`}
                        type="button"
                        onClick={() => { close(); onOpenKeepsake && onOpenKeepsake(k); }}
                        data-testid="profile-panel-keepsake-item"
                        className="w-full flex items-center gap-3 rounded-xl border border-pink-100 bg-pink-50/40 px-3 py-2.5 text-left hover:border-pink-300 hover:bg-pink-50 transition-colors"
                      >
                        <span className="text-xl flex-none">{k.kind === "anniversary" ? "🏆" : "🎂"}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-black text-slate-800 leading-tight">
                            {k.kind === "anniversary" ? `${k.years}-Year Anniversary` : "Birthday Card"}
                          </span>
                          <span className="block text-[12px] text-slate-500">{dt}</span>
                        </span>
                        <span className="flex-none text-[11px] font-black text-pink-600 bg-pink-100 rounded-full px-2.5 py-1">🎈 {k.signature_count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sign out — bottom-pinned, red-accent so it reads as the
                "exit" affordance. Triggers the goodbye splash on click. */}
            {onSignOut && (
              <div className="px-4 py-4 border-t border-slate-100 bg-slate-50/50">
                <button
                  type="button"
                  onClick={() => { close(); onSignOut && onSignOut(); }}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-rose-200 hover:bg-rose-50 active:scale-[0.99] transition-all text-rose-700 font-black text-sm flex items-center justify-center gap-2 shadow-sm"
                  data-testid="profile-panel-signout"
                >
                  <SignOut weight="bold" className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            )}
          </aside>
        </>
      )}
    </>
  );
}

// Internal panel row helper — kept inline to keep the side-panel
// styling self-contained.
function PanelRow({ testid, emoji, label, onClick, accent = "slate", dot = false }) {
  const isAmber = accent === "amber";
  const isRose = accent === "rose";
  const textCls = isAmber ? "text-amber-700 hover:bg-amber-50"
                : isRose  ? "text-rose-700 hover:bg-rose-50"
                : "text-slate-700 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full px-5 py-3 text-left text-sm font-bold flex items-center gap-3 transition-colors ${textCls}`}
    >
      <span className="text-lg flex-shrink-0" style={{ width: "1.4rem", textAlign: "center" }}>{emoji}</span>
      <span className="flex-1">{label}</span>
      {dot && (
        <span
          className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shadow-[0_0_6px_rgba(244,63,94,0.6)]"
          aria-label="Something new to see"
          data-testid={testid ? `${testid}-dot` : "panel-row-dot"}
        />
      )}
    </button>
  );
}

// Five Below brand-restricted palette for tile accents. Each "theme" is a
// gradient pair (icon chip) + a soft tinted shadow + a hover ring. Picking
// from this set keeps the home screen visually cohesive — no more pastel
// rainbow of mismatched bubbles. (user feedback 2026-02-22 "fix tile layout
// + colors", refreshed 2026-02-22 round 2 to a cleaner cross-tile palette).
const TILE_THEMES = {
  blue:    { from: "#3B82F6", to: "#0048B3", chipShadow: "0 6px 14px -4px rgba(0,102,255,0.45)",  glow: "rgba(0,102,255,0.10)"  },
  yellow:  { from: "#FACC15", to: "#F59E0B", chipShadow: "0 6px 14px -4px rgba(250,204,21,0.45)", glow: "rgba(250,204,21,0.10)" },
  pink:    { from: "#F472B6", to: "#9D174D", chipShadow: "0 6px 14px -4px rgba(236,72,153,0.45)", glow: "rgba(236,72,153,0.12)" },
  navy:    { from: "#1E293B", to: "#0F172A", chipShadow: "0 6px 14px -4px rgba(15,23,42,0.40)",   glow: "rgba(15,23,42,0.08)"   },
  teal:    { from: "#2DD4BF", to: "#0D9488", chipShadow: "0 6px 14px -4px rgba(13,148,136,0.40)", glow: "rgba(13,148,136,0.10)" },
  slate:   { from: "#94A3B8", to: "#475569", chipShadow: "0 6px 14px -4px rgba(71,85,105,0.30)",  glow: "rgba(71,85,105,0.08)"  },
  rose:    { from: "#FB7185", to: "#BE123C", chipShadow: "0 6px 14px -4px rgba(225,29,72,0.40)",  glow: "rgba(225,29,72,0.10)"  },
  violet:  { from: "#A78BFA", to: "#6D28D9", chipShadow: "0 6px 14px -4px rgba(124,58,237,0.40)", glow: "rgba(124,58,237,0.10)" },
  amber:   { from: "#FBBF24", to: "#B45309", chipShadow: "0 6px 14px -4px rgba(245,158,11,0.45)", glow: "rgba(245,158,11,0.12)" },
  // Orange added 2026-05-22 (round 2). Genuinely red-orange rather than
  // yellow-orange so it stops competing with the Email Contest gold and the
  // What's Up violet. Used by the District tile.
  orange:  { from: "#FB923C", to: "#C2410C", chipShadow: "0 6px 14px -4px rgba(234,88,12,0.45)",  glow: "rgba(234,88,12,0.12)"  },
};

const ChoiceTile = ({ icon: Icon, title, subtitle, theme = "blue", onClick, testid, badgeCount = 0, imageUrl = "", delay = 0, liveCount = null, liveLabel = "today", liveLabelZero = "today", liveUnit = "", alert = null, wide = false, personality = null, alive = false }) => {
  const t = TILE_THEMES[theme] || TILE_THEMES.blue;
  return (
    <button
      onClick={() => { tap("light"); playTap(); onClick && onClick(); }}
      className={`fb-enter group relative w-full rounded-2xl bg-white p-4 sm:p-4 flex flex-col items-start gap-2 text-left transition-all active:scale-[0.97] hover:-translate-y-0.5 overflow-hidden ${
        // Odd-tile alignment for the mobile 2-col grid.
        wide ? "col-span-2 lg:col-span-1" : ""
      }`}
      style={{
        "--fb-delay": `${delay}ms`,
        // Apple-clean 2026-08-06 pass: single soft elevation, no
        // colored glow, hairline border. Was double-shadow + colored
        // blob glow that made every tile visually fight for attention.
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 2px 8px -2px rgba(15,23,42,0.05)",
        border: "1px solid rgba(148,163,184,0.14)",
        minHeight: 100,
      }}
      data-testid={testid}
    >
      {/* Chevron affordance — hidden when a live-count chip already
          occupies the same corner. */}
      {(liveCount === null || liveCount === undefined) && (
        <span
          className="absolute top-3 right-3 z-20 inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-50 text-slate-400 transition-all group-hover:bg-blue-600 group-hover:text-white group-hover:translate-x-0.5"
          aria-hidden
        >
          <ArrowRight weight="bold" className="w-3.5 h-3.5" />
        </span>
      )}

      {/* Alert dot — pulsing red circle in the top-LEFT corner of the tile
          when something needs attention (e.g. May Contest pacing is under
          the 30% tier and tickets are about to be lost, Heart Balloons way
          behind pace, etc.). Falls in an empty corner so it never collides
          with the icon chip or the live count chip. Tooltip surfaces the
          reason on hover. */}
      {alert && (
        <span
          className="absolute top-2 left-2 z-20 inline-flex items-center justify-center"
          role="status"
          aria-label={typeof alert === "string" ? alert : "Needs attention"}
          title={typeof alert === "string" ? alert : "Needs attention"}
          data-testid={`${testid}-alert`}
        >
          <span
            className="absolute inset-0 rounded-full bg-rose-500 opacity-60 animate-ping"
            aria-hidden
          />
          <span className="relative w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white" aria-hidden />
        </span>
      )}

      {/* Live count chip — only renders when liveCount is a number. Shows the
          user's running daily emails total in real time so the Collect Emails
          tile reads as a tiny dashboard, not just a navigation button.
          Design 2026-07-01 (user ask: "in progress buttons are very
          inconsistent, consistency is key"): unified pill treatment.
          Always uses the tile's gradient background + white text with the
          same fixed height (h-5 = 20px), padding, border-radius and font
          size regardless of the count value. Zero state gets a subdued
          matte version of the same gradient so the shape stays identical
          — no more color/border flipping between "0" and ">0" tiles. */}
      {liveCount !== null && liveCount !== undefined && (
        <span
          className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 h-5 px-2 rounded-full text-[9px] font-black uppercase tracking-wider shadow-sm max-w-[68%]"
          style={{
            background: `linear-gradient(135deg, ${t.from}, ${t.to})`,
            color: "#FFFFFF",
            opacity: liveCount > 0 ? 1 : 0.55,
            fontFamily: "'Five Below Gothic', Chivo, sans-serif",
          }}
          data-testid={`${testid}-live-count`}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 bg-white"
            style={{
              animation: liveCount > 0 ? "fbPulseOnline 1.8s infinite" : "none",
            }}
            aria-hidden
          />
          <span className="tabular-nums truncate leading-none">{liveCount}{liveUnit} {liveCount > 0 ? liveLabel : liveLabelZero}</span>
        </span>
      )}

      {/* Icon chip (or photo) — gradient background, white icon, subtle
          shadow that matches the chip color so depth reads even on mobile.
          If `personality` is set, we render the animated character INSIDE
          the chip in place of the plain icon (same 40-48px footprint —
          no tile size change, per user ask 2026-07-02). */}
      <div
        className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105 group-hover:-rotate-3"
        style={{
          background: imageUrl ? "transparent" : `linear-gradient(135deg, ${t.from}, ${t.to})`,
          boxShadow: imageUrl ? "none" : t.chipShadow,
        }}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="w-full h-full object-cover rounded-xl" />
        ) : personality ? (
          <TilePersonality kind={personality} alive={alive} testId={`${testid}-personality`} />
        ) : (
          <Icon weight="bold" className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        )}
        {badgeCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center text-[9px] font-black text-white shadow-md ring-2 ring-white animate-pulse tabular-nums"
            style={{ backgroundColor: "#DC2626" }}
            data-testid={`${testid}-badge`}
            aria-label={`${badgeCount} new`}
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </div>

      {/* Title + subtitle */}
      <div className="relative z-10 flex-1 min-w-0 w-full">
        <p
          className="font-black text-sm sm:text-base leading-tight tracking-tight text-slate-900 line-clamp-1"
          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
        >
          {title}
        </p>
        <p className="text-[10px] sm:text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-1 sm:line-clamp-2">
          {badgeCount > 0 && title === "Inbox" ? (
            <span className="font-bold" style={{ color: "#DC2626" }}>
              {badgeCount} new {badgeCount === 1 ? "message" : "messages"}
            </span>
          ) : subtitle}
        </p>
      </div>
    </button>
  );
};

// Greeting changes by hour of day (America/Denver) to add a bit of personality.
const getGreeting = () => {
  const h = parseInt(
    new Intl.DateTimeFormat("en-US", { timeZone: "America/Denver", hour: "2-digit", hour12: false })
      .format(new Date())
      .replace(/\D/g, ""),
    10
  );
  if (h < 5) return "Late shift?";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Late shift?";
};

// ---------------------------------------------------------------------------
// 🚀 Launch Day — district-wide go-live celebration. Renders nothing on any
// other day. Calendar-driven (MST) so admins don't have to flip a toggle:
// at midnight MST on May 27 the banner self-removes. Confetti fires once
// per device on the first home-page paint of the day (localStorage flag).
//
// To extend the celebration (e.g. soft-launch week) add more YYYY-MM-DD
// strings to LAUNCH_DAYS_MST below.
// ---------------------------------------------------------------------------
const LAUNCH_DAYS_MST = new Set(["2026-05-26"]);
const LAUNCH_SPLASH_KEY = "ecDashLaunchSplash";
const LAUNCH_BANNER_DISMISS_KEY = "ecDashLaunchBannerDismiss";

function LaunchDayHero({ firstName }) {
  const todayMst = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  const isLaunchDay = LAUNCH_DAYS_MST.has(todayMst);
  const [showSplash, setShowSplash] = useState(false);
  // Per-device dismiss flag — keyed by day so a future multi-day launch
  // window doesn't carry over yesterday's dismiss. (User ask 2026-05-26
  // launch day v2: "I love the banner but let's make it to where you
  // can close it".)
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(LAUNCH_BANNER_DISMISS_KEY);
      if (!raw) return false;
      const days = new Set(JSON.parse(raw));
      return days.has(todayMst);
    } catch { return false; }
  });

  const closeBanner = () => {
    setDismissed(true);
    try {
      const raw = localStorage.getItem(LAUNCH_BANNER_DISMISS_KEY);
      const days = new Set(raw ? JSON.parse(raw) : []);
      days.add(todayMst);
      localStorage.setItem(LAUNCH_BANNER_DISMISS_KEY, JSON.stringify([...days]));
    } catch { /* quota */ }
  };

  useEffect(() => {
    if (!isLaunchDay) return undefined;
    // Once per device per launch day. We key on YYYY-MM-DD so a future
    // multi-day launch window (Mon → Fri) fires the splash once each day.
    let seenSet = new Set();
    try {
      const raw = localStorage.getItem(LAUNCH_SPLASH_KEY);
      if (raw) seenSet = new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    if (!seenSet.has(todayMst)) {
      setShowSplash(true);
      seenSet.add(todayMst);
      try { localStorage.setItem(LAUNCH_SPLASH_KEY, JSON.stringify([...seenSet])); } catch { /* quota */ }
      // Burst of confetti — short, celebratory, doesn't block taps.
      try {
        const fire = (opts) => confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.25 },
          ticks: 200,
          ...opts,
        });
        fire({ angle: 60, origin: { x: 0.1, y: 0.3 } });
        fire({ angle: 120, origin: { x: 0.9, y: 0.3 } });
        setTimeout(() => fire({ angle: 90, origin: { x: 0.5, y: 0.2 }, particleCount: 120 }), 220);
      } catch { /* canvas missing — skip */ }
      // Auto-dismiss the splash after 3.2s.
      const t = setTimeout(() => setShowSplash(false), 3200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isLaunchDay, todayMst]);

  // Fire a window event the moment the splash dismisses (either via the
  // 3.2s auto-timeout or a user tap). App.js listens for this so the
  // first-time tutorial WelcomePrompt waits to surface until the splash
  // is gone — otherwise the tutorial card buries the splash. (User
  // report 2026-05-26: "the quick tutorial is still covering the launch
  // day splash screen — need it to delay until they click out".)
  // Tracks the previous value of `showSplash` so we only dispatch on
  // the true→false transition (not the initial mount where it's
  // already false and the splash never ran).
  const splashWasOnRef = useRef(false);
  useEffect(() => {
    if (showSplash) {
      splashWasOnRef.current = true;
    } else if (splashWasOnRef.current) {
      splashWasOnRef.current = false;
      try { window.dispatchEvent(new CustomEvent("wow:launch-splash-done")); }
      catch { /* ignore */ }
    }
  }, [showSplash]);

  if (!isLaunchDay) return null;

  return (
    <>
      {/* Always-on ribbon for the day — sits above the celebration banner.
          Dismissable via the ⓧ; the dismiss is sticky per-device-per-day
          so closing it on Dom's iPhone doesn't hide it on his iPad. */}
      {!dismissed && (
        <div
          className="px-5 pt-1 flex-shrink-0 max-w-lg lg:max-w-6xl w-full mx-auto"
          data-testid="launch-day-banner"
        >
          <div
            className="relative overflow-hidden rounded-2xl px-4 py-3.5 pr-12 flex items-center gap-3 shadow-lg"
            style={{
              background:
                "linear-gradient(115deg, #7C3AED 0%, #2563EB 35%, #0EA5E9 65%, #10B981 100%)",
            }}
          >
            {/* Sparkles overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-80" aria-hidden>
              <span className="absolute top-2 left-8 w-1 h-1 rounded-full bg-white animate-pulse" />
              <span className="absolute top-6 left-28 w-0.5 h-0.5 rounded-full bg-yellow-200 animate-pulse" style={{ animationDelay: "0.4s" }} />
              <span className="absolute top-3 right-16 w-1 h-1 rounded-full bg-pink-200 animate-pulse" style={{ animationDelay: "0.9s" }} />
              <span className="absolute bottom-3 right-32 w-0.5 h-0.5 rounded-full bg-white animate-pulse" style={{ animationDelay: "1.3s" }} />
              <span className="absolute bottom-2 left-20 w-1 h-1 rounded-full bg-cyan-200 animate-pulse" style={{ animationDelay: "0.6s" }} />
            </div>
            <span className="relative text-3xl flex-shrink-0" aria-hidden>🚀</span>
            <div className="relative flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/85">
                District-wide launch day
              </p>
              <p
                className="text-lg sm:text-xl font-black text-white leading-tight"
                style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", textShadow: "0 1px 12px rgba(0,0,0,0.25)" }}
              >
                It's go time, ColoRADo West!
              </p>
              <p className="text-xs text-white/90 font-semibold mt-0.5">
                Every email today writes the story. Let go & have fun. ✨
              </p>
            </div>
            <button
              type="button"
              onClick={closeBanner}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/15 hover:bg-white/30 active:scale-95 flex items-center justify-center text-white transition-all"
              aria-label="Dismiss launch day banner"
              data-testid="launch-day-banner-close"
            >
              <X weight="bold" className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* One-time splash on the first launch-day paint. Full-screen, ~3s.
          z-[210] keeps it above the first-login tutorial WelcomePrompt
          (z-[200]) so launch-day excitement isn't buried behind the
          tour modal. (User report 2026-05-26: "the tutorial is covering
          up the launch day splash".) */}
      {showSplash && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center px-6"
          style={{
            background:
              "radial-gradient(circle at 50% 30%, rgba(124, 58, 237, 0.92) 0%, rgba(15, 23, 42, 0.96) 70%)",
            animation: "fadeIn 220ms ease-out forwards",
          }}
          data-testid="launch-day-splash"
          onClick={() => setShowSplash(false)}
          role="button"
          tabIndex={-1}
          aria-label="Dismiss launch day splash"
        >
          <div className="text-center" style={{ animation: "fb-splash-pulse 1.6s ease-out" }}>
            <div className="text-7xl mb-3" aria-hidden>🚀</div>
            <p
              className="text-[11px] uppercase tracking-[0.32em] font-black text-white/80"
            >
              Launch Day — ColoRADo West District
            </p>
            <h1
              className="mt-2 text-5xl sm:text-6xl font-black text-white leading-[0.95]"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.02em", textShadow: "0 2px 24px rgba(124,58,237,0.6)" }}
            >
              {firstName ? `Let's go, ${firstName}!` : "Let's go, crew!"}
            </h1>
            <p className="mt-3 text-base sm:text-lg font-semibold text-white/95 max-w-md mx-auto">
              11 stores. One mission. Make today legendary.
            </p>
            <p className="mt-4 text-[10px] uppercase tracking-[0.28em] font-bold text-white/60">
              tap anywhere to continue
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// Renders the "What are you doing today?" picker. Used on post-login and as an in-app Home view.
export default function HomePage({ user, crew = [], onPick, onSwitchUser, onOpenV2Preview }) {
  const isManager = !!user?.is_manager;
  const isAdmin = !!user?.is_admin;
  const isDM = !!user?.is_district_manager;
  // Home Screen Layout — 2026-08-01 feature. Fetches `hidden_tiles`
  // for this user's home store and used by `isTileHidden(id)` below
  // to short-circuit any tile the admin/manager has hidden. Refreshes
  // when Settings dispatches `wow:tile-prefs-changed` so a manager
  // toggling a tile in Settings sees the home update live.
  const [hiddenTiles, setHiddenTiles] = useState(new Set());
  useEffect(() => {
    const storeId = user?.home_store_id;
    if (!storeId) return undefined;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/store-tile-prefs/${storeId}`);
        if (!cancelled) setHiddenTiles(new Set(r.data.hidden_tiles || []));
      } catch { /* stay with empty set — everything visible on failure */ }
    };
    load();
    const onChange = () => load();
    window.addEventListener("wow:tile-prefs-changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("wow:tile-prefs-changed", onChange);
    };
  }, [user?.home_store_id]);
  const isTileHidden = (id) => hiddenTiles.has(id);

  // Training Store Manager — read-only district shadow (no mirror).
  // Surfaces the District tile but every mirror affordance is hidden
  // inside DistrictDashboard. (User ask 2026-05-25.)
  const isTSM = !!user?.is_training_sm;
  // Ghost viewers — silent omniscient access (MM / CEM / PTSL). They see
  // every story but never appear in the author's "Seen" list. Position
  // names match the canonical strings in TeamPage.js's POSITION_ABBREV.
  const GHOST_POSITIONS = new Set([
    "Merchandise Manager",
    "Customer Experience Manager",
    "Part Time Support Lead",
  ]);
  const isGhostViewer = GHOST_POSITIONS.has((user?.position || "").trim());
  // Stories access predicate — mirrors backend tiered visibility:
  //   3094 crew (any role) → see/post in the 3094 feed.
  //   Sister-store SMs + DM + admins → see/post in the manager-tier feed.
  //   MM / CEM / PTSL anywhere → see EVERY story silently.
  //   Sister-store regular crew → no Stories at all.
  // (User spec 2026-05-21.)
  const homeStore = user?.home_store_id || "";
  const isOn3094 = homeStore === "store_3094" || !homeStore;
  // Stories visibility — wow crew at sister stores can now see their own
  // store's manager-tier stories (user ask 2026-05-23 "stories should be
  // able to be viewed by stores wow crew can react but can not post").
  // Server already filters by tier/store; we just always render the row
  // and let it self-hide when empty.
  const canUseStories = true;
  // Story POSTING — separate gate. 3094 anyone can post; sister-store
  // only SM / DM / admin / ghost viewer can post. StoriesRow mirrors this
  // internally; we use it here to hide the profile-menu "Post a story"
  // entry so wow crew at sister stores never see a dead button.
  const canPostStory = isOn3094 || isDM || isAdmin || isGhostViewer || (isManager && homeStore && homeStore !== "store_3094");
  // Mirror mode = the App's effectiveUser wrapper flagged this session.
  // Currently unused for tile gating (Branding/Appearance/Hours are
  // admin-gated inside SettingsPage which receives the RAW user, so an
  // admin in mirror mode still has full edit rights AND a real sister-
  // store SM only sees their non-admin slice). Kept here in case future
  // mirror-only UX (banners, copy tweaks) needs it.
  // eslint-disable-next-line no-unused-vars
  const isMirroring = !!user?._mirroring;
  const greeting = getGreeting();
  const { theme } = useTheme();
  // Look up the current user's uploaded photo so we can swap it into the "My Profile" tile.
  const myPhoto = crew.find((c) => c.id === user?.crew_id)?.photo_url || "";

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  // Bug-report modal — opens from the header chat-bubble icon or from
  // Settings → footer. Single piece of state so we can mount the modal
  // once at the bottom and reuse it everywhere. (User ask 2026-05-21.)
  const [showReportIssue, setShowReportIssue] = useState(false);
  // Penny Rounding removed 2026-10-04 (feature retired company-wide).

  // Global event hooks — let the CreditByDom profile card (and any future
  // shared component) request the two most common follow-ups: open a DM
  // with someone, or fire the ReportIssueModal. Keeps the implementation
  // decoupled from prop drilling. (User ask 2026-05-25: "show Message
  // Dom or Open a Ticket buttons" inside the founder profile card.)
  useEffect(() => {
    const onOpenDm = (e) => {
      const crewId = e?.detail?.crewId;
      if (!crewId) return;
      onPick && onPick("inbox", { initialDmCrewId: crewId });
    };
    const onOpenTicket = () => setShowReportIssue(true);
    window.addEventListener("wowapp:open-dm", onOpenDm);
    window.addEventListener("wowapp:open-ticket", onOpenTicket);
    return () => {
      window.removeEventListener("wowapp:open-dm", onOpenDm);
      window.removeEventListener("wowapp:open-ticket", onOpenTicket);
    };
  }, [onPick]);
  // EOD report modal — `null` when closed, otherwise `{ date, store_id }`
  // populated from the `eod_ready` notification the user just tapped.
  const [eodReport, setEodReport] = useState(null);
  const [storyComposerOpen, setStoryComposerOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [] });
  // Live "X today" count for the Collect Emails tile. Fetched once on mount
  // and refreshed every 30s so the tile reads as a tiny dashboard. Only crew
  // (not managers) see this — managers don't tally personally.
  const [todayCount, setTodayCount] = useState(isManager ? null : 0);
  // Manager Dashboard tile live KPI (Wave A.5 2026-06-13). Reuses the
  // EXISTING `/api/tally/{today}` poll — no new endpoint, no new
  // interval, no Home Page layout change. Only the chip on the
  // existing Manager Dashboard tile gets a number to show.
  const [managerCaptureRate, setManagerCaptureRate] = useState(null);

  // ---------------------------------------------------------------
  // Home top-of-page summary cards (Tasks + Daily Passcode) — 2026-XX
  // Small state block for the two dashboard-style cards that
  // replaced the yellow tasks bar + dashed passcode bar. Each card
  // opens its corresponding controlled Sheet on the invisible
  // YourTasksTodayCard / DailyPasscodeCard instances mounted further
  // down in the tree. Progress arc data is fetched here so the
  // arcs stay accurate even while their Sheets are closed.
  // ---------------------------------------------------------------
  const [tasksSummary, setTasksSummary] = useState({ done: 0, total: 0, inProgress: 0 });
  const [passcodeReady, setPasscodeReady] = useState(false);
  const [tasksSheetOpen, setTasksSheetOpen] = useState(false);
  const [passcodeSheetOpen, setPasscodeSheetOpen] = useState(false);

  useEffect(() => {
    if (!user?.crew_id) return undefined;
    let cancelled = false;
    const todayLocal = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    const load = async () => {
      // Tasks summary — same endpoint YourTasksTodayCard uses so we
      // benefit from any shared HTTP cache and only pay one extra
      // round-trip on load.
      try {
        const r = await axios.get(`${API}/zone-sheets/my/${todayLocal}`, {
          params: { actor_id: user.crew_id, store_id: user.home_store_id || "" },
        });
        if (cancelled) return;
        const tasks = Array.isArray(r.data?.tasks) ? r.data.tasks : [];
        const done = tasks.filter((t) => t.status === "completed" || t.status === "manager_verified").length;
        // In-progress = tasks that have moved past "not started" but
        // haven't landed yet. Feeds the lighter cyan-300 arc on the
        // Tasks tile so crew see "something's already moving" at a
        // glance without opening the sheet. (User ask 2026-07-02.)
        const inProgress = tasks.filter((t) => t.status === "in_progress" || t.status === "awaiting_verify").length;
        setTasksSummary({ done, total: tasks.length, inProgress });
      } catch {
        if (!cancelled) setTasksSummary({ done: 0, total: 0, inProgress: 0 });
      }
      // Passcode ready — same endpoint DailyPasscodeCard uses.
      try {
        const r = await axios.get(`${API}/passcodes/me`, { params: { crew_id: user.crew_id } });
        if (cancelled) return;
        setPasscodeReady(!!(r.data && r.data.code));
      } catch {
        if (!cancelled) setPasscodeReady(false);
      }
    };
    load();
    const t = setInterval(load, 45_000);
    // Live-refresh when a task transition SSE arrives so the arc
    // updates instantly (no wait for the 45s poll).
    const onSSE = (e) => {
      const evt = e.detail || {};
      if (evt.kind === "coverage_task_update" || evt.kind === "passcode_claimed") load();
      // 2026-08-27 — when ANY manager approves/denies a PIN reset,
      // stamp `_resolved` so PinResetNotifRow renders a permanent
      // "Approved by [Name]" audit line. Timer removes the row after
      // ~8s so the notif drawer doesn't accumulate stale entries.
      if (evt.kind === "pin_reset_resolved" && evt.request_id) {
        setNotifications((prev) => prev.map((n) =>
          (n?.type === "pin_reset_request" && n?.request_id === evt.request_id)
            ? { ...n, _resolved: {
                resolution: evt.resolution || "approved",
                by: evt.resolved_by || "another manager",
                at: evt.resolved_at || new Date().toISOString(),
              } }
            : n
        ));
        window.setTimeout(() => {
          setNotifications((prev) => prev.filter(
            (n) => !(n?.type === "pin_reset_request" && n?.request_id === evt.request_id)
          ));
        }, 8000);
      }
    };
    window.addEventListener("wow:notification", onSSE);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("wow:notification", onSSE);
    };
  }, [user?.crew_id, user?.home_store_id]);
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/tally/${today}`);
        if (cancelled) return;
        if (!isManager) {
          const c = r.data?.crew_counts?.[user.crew_id] || 0;
          setTodayCount(c);
        } else {
          // Round to whole percent for the small chip (decimal would
          // wrap on the tiny pill). Below the chip threshold (no tx
          // yet today) we still set 0 so the chip renders the
          // "0% today" empty state instead of nothing — matches the
          // crew tile pattern.
          const rate = r.data?.capture_rate || 0;
          setManagerCaptureRate(Math.round(rate * 100));
        }
      } catch {
        /* ignore — tile will still show "0 today" */
      }
    };
    load();
    // Visibility-gated: skip the tally poll while the PWA is hidden, then
    // re-fetch the moment it foregrounds. Saves battery on iPhone and
    // prevents the "wake-up storm" the user reported 2026-05-13.
    const iv = setInterval(() => { if (!document.hidden) load(); }, 30_000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    // Instant wake — CrewTallyPage broadcasts `wow:tally-updated` the
    // moment a scan lands (see CrewTallyPage.js handleTally). We use
    // its detail payload to flip the Collect Emails tile from snoozing
    // → dancing without waiting for the next 30s poll cycle. Also
    // fires `load()` in the background so the authoritative store-wide
    // capture rate catches up for managers. (User bug 2026-02: "tile
    // still reads snoozing… took a minute to update after we scanned
    // our barcode".)
    const onTallyUpdated = (e) => {
      const detail = e?.detail || {};
      if (!isManager) {
        // Crew tile — trust the crew-local count from the POST
        // response. Falls back to a refresh if the payload was empty.
        if (typeof detail.my_count === "number") {
          setTodayCount(detail.my_count);
        } else {
          load();
        }
      } else if (typeof detail.capture_rate === "number") {
        // Manager tile — the POST response carries the fresh store
        // capture rate. Round to whole percent to match the chip.
        setManagerCaptureRate(Math.round(detail.capture_rate * 100));
      } else {
        load();
      }
    };
    window.addEventListener("wow:tally-updated", onTallyUpdated);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("wow:tally-updated", onTallyUpdated);
    };
  }, [isManager, user?.crew_id]);

  // Live ticket total for the May Contest tile — visible to everyone in the
  // store. Refreshed every 60s so completed weeks lock in automatically.
  const [contestSummary, setContestSummary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/contest/may-2026`);
        if (!cancelled) setContestSummary(r.data);
      } catch { /* tile will fall back to a generic subtitle */ }
    };
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Heart Balloon Sweepstakes fetch removed 2026-05-19 (campaign + page deleted).

  // Manager-only — fetch today's checkpoints to flag overdue 2pm/5pm/8pm
  // entries on the Manager Dashboard tile.
  const [todayCheckpoints, setTodayCheckpoints] = useState(null);
  useEffect(() => {
    if (!isManager) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
        const r = await axios.get(`${API}/biz/${today}`);
        if (!cancelled) setTodayCheckpoints(r.data);
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [isManager]);

  // ---- Alert computations -----------------------------------------------
  // Each tile gets a tiny pulsing red dot when something needs attention.
  // Logic kept lean — surface real, actionable signals only:
  //   • May Contest: active week pacing < 30% (about to lose tickets)
  //   • Heart Balloons: campaign in last 7 days AND under 70% to goal
  //   • Manager Dashboard (mgr only): an expected checkpoint slot is past
  //     its scheduled MST hour but hasn't been entered yet
  const contestAlert = (() => {
    if (!contestSummary || !contestSummary.is_active) return null;
    const active = (contestSummary.weeks || []).find((w) => w.status === "active");
    if (!active) return null;
    if (active.transactions === 0) return null; // no data yet, nothing to flag
    if (active.rate < 0.30) {
      return `This week pacing ${(active.rate * 100).toFixed(0)}% — under 30% means 0 tickets`;
    }
    return null;
  })();
  // Heart Balloons alert removed 2026-05-19 with the campaign cleanup.

  // Milestone celebration — fires a full-screen confetti + chime when:
  //   1. The contest ticket count ticks up vs last poll (everyone)
  //   2. The store crosses the daily goal rate (manager view)
  // We snapshot the relevant value on first observation so the celebration
  // only fires on actual transitions, not the first paint. (Desktop-fun
  // pack, 2026-05-21.)
  const [milestone, setMilestone] = useState(null);
  // Triple-tap detector on the Collect Emails tile (Spider-Man theme
  // easter egg). We use a ref for the tap count so setState timing
  // doesn't race the 350ms window. On 3 taps → thwip + stay put.
  // On 1-2 taps → after the window closes we navigate normally, so
  // day-to-day single-tap behavior only feels ~350ms delayed once
  // per session (the timer clears itself as soon as navigation runs).
  const emailTapRef = useRef({ count: 0, timer: null });
  const emailTileTap = () => {
    const s = emailTapRef.current;
    s.count += 1;
    if (s.timer) clearTimeout(s.timer);
    if (s.count >= 3) {
      s.count = 0;
      s.timer = null;
      window.dispatchEvent(new Event("spider:thwip"));
      return;
    }
    // Widened from 350ms → 550ms (2026-02) — the original was too
    // tight for humans and only power-users could trigger the
    // easter egg. 550ms is still fast enough that a single
    // deliberate tap doesn't feel laggy.
    s.timer = setTimeout(() => {
      s.count = 0;
      s.timer = null;
      onPick("tally");
    }, 550);
  };
  const lastTickets = useRef(null);
  useEffect(() => {
    if (!contestSummary) return;
    const total = contestSummary.total_tickets || 0;
    if (lastTickets.current === null) { lastTickets.current = total; return; }
    if (total > lastTickets.current) {
      const won = total - lastTickets.current;
      setMilestone({
        title: "🎟️ TICKET WON!",
        subtitle: `${won} new contest ticket${won === 1 ? "" : "s"} locked in`,
      });
    }
    lastTickets.current = total;
  }, [contestSummary]);
  const lastGoalState = useRef(null);
  useEffect(() => {
    if (!todayCheckpoints) return;
    const cps = todayCheckpoints.checkpoints || {};
    const latest = cps["8pm"] || cps["5pm"] || cps["2pm"];
    if (!latest) return;
    const emails = latest.emails || todayCheckpoints.emails || 0;
    const tx = latest.transactions || 0;
    if (!tx) return;
    const rate = emails / tx;
    const atGoal = rate >= (theme?.goal_rate || 0.35);
    if (lastGoalState.current === null) { lastGoalState.current = atGoal; return; }
    if (atGoal && !lastGoalState.current) {
      setMilestone({
        title: "🎯 GOAL HIT!",
        subtitle: `${(rate * 100).toFixed(0)}% capture rate · keep going!`,
      });
      // Spider-Man theme easter egg: swing across screen with a POW
      // sound when the store hits daily goal. No-ops when theme is
      // off (event listener isn't wired unless SpiderManTheme is
      // active).
      window.dispatchEvent(new Event("spider:pow"));
    }
    lastGoalState.current = atGoal;
  }, [todayCheckpoints, theme?.goal_rate]);
  const dashboardAlert = (() => {
    if (!isManager || !todayCheckpoints) return null;
    // /api/biz/{date} returns `checkpoints` as an object keyed by slot name
    // ("2pm" / "5pm" / "8pm"). A slot counts as "entered" if it has any
    // value filled in (sales OR transactions).
    const cps = todayCheckpoints.checkpoints || {};
    // A slot counts as "entered" if ANY of its KPI cells has a value.
    // Eyes on the Biz stores `reads` / `sales` / `bopis` (and the legacy
    // tally pipeline also writes `transactions`). Previously this only
    // checked `sales`/`transactions` — managers who entered reads + BOPIS
    // at 2pm / 5pm (skipping sales until 8pm) saw the overdue red banner
    // stay forever. (User report 2026-05-23: "both of my readings today
    // were late, but the overdue alert doesn't disappear after I put the
    // readings in".)
    const isFilled = (slot) => {
      const c = cps[slot];
      if (!c) return false;
      const hasVal = (v) => v !== undefined && v !== null && v !== "";
      return hasVal(c.sales) || hasVal(c.transactions) || hasVal(c.reads) || hasVal(c.bopis);
    };
    // Build expected slots from current MST hour. After 2pm → expect 2pm,
    // after 5pm → expect 2pm + 5pm, after 8pm → expect all three.
    const nowMst = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Denver" }));
    const hour = nowMst.getHours();
    const due = [];
    if (hour >= 14) due.push("2pm");
    if (hour >= 17) due.push("5pm");
    if (hour >= 20) due.push("8pm");
    const missing = due.filter((slot) => !isFilled(slot));
    if (missing.length === 0) return null;
    return `${missing.join(" + ")} checkpoint${missing.length > 1 ? "s" : ""} overdue`;
  })();

  // Mother's Day Heart Balloon Sweepstakes — store-level cumulative counter
  // toward a goal of 76. Refreshes every 30s so taps from the register reflect
  // on every screen quickly.
  const fetchNotifications = useCallback(async () => {
    if (!user?.crew_id) return;
    try {
      const params = new URLSearchParams({ crew_id: user.crew_id, is_manager: String(isManager), limit: "30" });
      const res = await axios.get(`${API}/notifications?${params.toString()}`);
      setNotifications(res.data || []);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  }, [user?.crew_id, isManager]);

  const fetchInboxUnread = useCallback(async () => {
    if (!user?.crew_id) return;
    try {
      const res = await axios.get(`${API}/inbox/${user.crew_id}/unread-count?is_manager=${isManager}`);
      setInboxUnread(res.data?.unread || 0);
    } catch (e) {
      console.error("Failed to fetch inbox unread:", e);
    }
  }, [user?.crew_id, isManager]);

  useEffect(() => {
    fetchNotifications();
    fetchInboxUnread();
    // Birthdays & anniversaries only change at midnight MST — poll every 5min is plenty.
    const loadCelebrations = () => {
      axios.get(`${API}/celebrations/today`)
        .then((r) => setCelebrations(r.data || { birthdays: [], anniversaries: [] }))
        .catch(() => {});
    };
    loadCelebrations();
    const id = setInterval(() => {
      fetchNotifications();
      fetchInboxUnread();
    }, 20000);
    const celebId = setInterval(loadCelebrations, 5 * 60 * 1000);
    // Refetch when admin enters/leaves Store Mirror so this store's
    // celebrations + bell counts replace the previous store's. (2026-05-19
    // Store Mirror fix.)
    const onScope = () => {
      setTodayCount(isManager ? null : 0);
      setContestSummary(null);
      setTodayCheckpoints(null);
      // Reset milestone snapshots so the next contestSummary / goal poll
      // doesn't fire a celebration based on the DIFFERENCE between the
      // previous scope's totals and the new scope's totals.
      // (User report 2026-05-22: viewing another store then logging back
      // into their own store fired a fake "🎟️ 3 new contest tickets
      // locked in" celebration — actually just the delta between Store A's
      // 3 tickets and the real store's 5.)
      lastTickets.current = null;
      lastGoalState.current = null;
      fetchNotifications();
      fetchInboxUnread();
      loadCelebrations();
    };
    window.addEventListener("wow:scope-changed", onScope);
    return () => {
      clearInterval(id);
      clearInterval(celebId);
      window.removeEventListener("wow:scope-changed", onScope);
    };
  }, [fetchNotifications, fetchInboxUnread, isManager]);

  // Fire confetti every time the birthday/anniversary crew opens HomePage today.
  // This intentionally re-fires on each HomePage mount (per user request) so their
  // day feels extra — but only THEY see it (non-celebrating users never get confetti).
  useEffect(() => {
    if (!user?.crew_id || !celebrations?.date) return;
    const isMine = [
      ...(celebrations.birthdays || []),
      ...(celebrations.anniversaries || []),
    ].some((p) => p.id === user.crew_id);
    if (!isMine) return;

    // Multi-burst confetti from both bottom corners — Five Below brand colors.
    const end = Date.now() + 1800;
    const colors = ["#0066FF", "#FACC15", "#EC4899", "#16A34A", "#FFFFFF"];
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0, y: 0.85 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1, y: 0.85 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, [user?.crew_id, celebrations]);

  const handleMarkAllRead = async () => {
    if (!user?.crew_id) return;
    try {
      const params = new URLSearchParams({ crew_id: user.crew_id, is_manager: String(isManager) });
      await axios.put(`${API}/notifications/read-all?${params.toString()}`);
      fetchNotifications();
    } catch (e) {
      console.error("Failed to mark all read:", e);
    }
  };

  const unreadNotifications = notifications.filter((n) => !n.read);
  const unreadCount = unreadNotifications.length;

  // Screenshot-attempt detection. Mounted here too because the first
  // post-login render comes from inside LoginPage, before App.js's user
  // state is set. Idempotent across remounts. (User ask 2026-05-21.)
  useScreenshotDetect(user?.crew_id, "home");

  // Combined pulse: bell notifications + inbox (new messages, mentions, reactions).
  // When either increments the cue fires a ding + haptic buzz.
  const combinedUnread = unreadCount + inboxUnread;

  // Mute toggle — persisted in localStorage under `ecDashCueMuted` so the preference
  // survives reloads and is shared across pages.
  const [cueMuted, setCueMuted] = useState(() => {
    try { return localStorage.getItem("ecDashCueMuted") === "1"; } catch { return false; }
  });
  const toggleCueMuted = () => {
    setCueMuted((m) => {
      const next = !m;
      try { localStorage.setItem("ecDashCueMuted", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  useNotificationCue(combinedUnread, { enabled: !cueMuted });

  // May the 4th glitch theme is active when the deep-space preset is on.
  // Drives a top-level body class so the entire home tree picks up the
  // hologram / blink / scanline accents we added in index.css. Auto-clears
  // the moment the admin flips themes — no manual cleanup needed.
  const may4Active = (theme?.background_color || "").toUpperCase() === "#050418";
  // Patriotic theme animations — activated by the color combo unique to
  // the "Patriotic" settings preset (red primary + blue accent, see
  // SettingsPage THEME_PRESETS). Adds twinkling stars + a red/white/blue
  // ribbon in CSS, and periodic low-intensity confetti bursts from the
  // <PatrioticFireworks /> component. Detects by BOTH colors so a
  // custom red/blue theme doesn't accidentally trigger.
  // (User ask 2026-XX "add animations to the existing patriotic theme".)
  const patrioticActive =
    (theme?.primary_color || "").toUpperCase() === "#DC2626" &&
    (theme?.accent_color || "").toUpperCase() === "#2563EB";
  // Goldie 6-day drop schedule (Mon 5/11 → Sat 5/16) — auto-rolls each
  // day and unlocks one more piece of the theme. See useGoldieStage.js
  // for the schedule + manual override hooks.
  const goldieStage = useGoldieStage(theme?.background_color);
  const goldieClasses = goldieStage > 0
    ? `goldie-active ${Array.from({ length: goldieStage }, (_, i) => `goldie-stage-${i + 1}`).join(" ")}`
    : "";
  // Dumpling tile removed 2026-05-19 (page deleted with the Heart Balloons + Dumpling cleanup).

  return (
    <div
      className={`fixed inset-0 flex flex-col lg:static lg:min-h-screen overflow-y-auto overflow-x-hidden lg:overflow-auto ${may4Active ? "may4-active" : ""} ${patrioticActive ? "patriotic-active" : ""} ${goldieClasses}`}      style={{
        // 100dvh + fixed positioning locks the home screen on mobile while
        // still letting the tile grid + banner stack scroll vertically when
        // it overflows (Zebra TC52 portrait is only ~640px tall — the 7-tile
        // grid plus launch banner + celebration banner spills past the
        // viewport). User report 2026-05-26: "make the app scrollable on
        // the zebras because the tiles are too big". Horizontal still
        // locked to prevent accidental side-swipes. Desktop keeps `static`
        // positioning so the layout flows naturally alongside headers.
        height: "100dvh",
        // Soft tinted background that picks up the active theme's primary
        // hue. `rgb(var(--primary-rgb) / N%)` blends the theme color over
        // white so even a Halloween orange or Holiday red stays subtle.
        // Falls back to the original FB blue gradient when --primary-rgb
        // isn't set yet (first paint before ThemeContext applies).
        background: "linear-gradient(180deg, rgb(var(--primary-rgb, 0 102 255) / 0.08) 0%, rgb(var(--primary-rgb, 0 102 255) / 0.03) 45%, var(--home-bg-end, rgba(245,245,244,1)) 100%)",
      }}
      data-testid="home-page"
    >
      {/* First-login push-notifications nudge for every WowCrew member.
          Auto-opens ~1.5s after mount when the user isn't subscribed
          yet and push CAN work on this device. Dismisses for 7 days on
          skip. (User ask 2026-02-27: "get all WowCrew to turn on
          notifications".) */}
      {/* PushNudge retired 2026-02-27 — was duplicating PushHardGate. */}
      <PushHardGate crewId={user?.crew_id || ""} />
      <WalkieFAB crewId={user?.crew_id || ""} />
      {/* May the 4th opening crawl — overlays the home screen on first
          load each session when the deep-space theme is on. Self-dismisses
          after ~9.5s; users can tap the Skip pill or anywhere else to bail. */}
      {may4Active && <May4Crawl storeName={theme?.app_name || "Wow Crew"} />}
      {/* Patriotic theme fireworks — subtle red/white/blue confetti bursts
          from off-screen every ~7s while the preset is active. Also drives
          the twinkling-star and ribbon CSS via the `patriotic-active`
          class on the root above. */}
      {patrioticActive && <PatrioticFireworks />}
      {/* Goldie 6-day drop — pieces unlock progressively (see
          useGoldieStage). Stage 2+ glitter, 4+ corner mascot, 5+ ticket
          floater. Toast announces each day's drop on first login.
          Greeting/heading recolor lives in CSS via the stage classes. */}
      <GoldieDropToast stage={goldieStage} />
      {goldieStage >= 2 && <GoldGlitter />}
      {goldieStage >= 4 && (
        <img
          src="/goldie-mascot.png?v=6"
          alt=""
          className="goldie-corner"
          aria-hidden="true"
          data-testid="goldie-corner-mascot"
        />
      )}
      {goldieStage >= 5 && (
        <img
          src="/golden-ticket.png?v=6"
          alt=""
          className="golden-ticket-floater"
          aria-hidden="true"
          data-testid="goldie-ticket-floater"
        />
      )}
      {/* Header */}
      <div className={`px-5 pt-5 sm:pt-6 pb-2 flex-shrink-0 ${patrioticActive ? "patriotic-ribbon" : ""}`}>
        <div className="max-w-lg lg:max-w-6xl w-full mx-auto flex items-start justify-between gap-3">
          <div className="min-w-0 flex-shrink-0">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--fb-blue)" }}>{greeting}</p>
            {/* First-name heading restored 2026-06-05 — user wants "Good
                morning Dom" on the home screen. Previously removed
                2026-05-26 based on a misread of "I don't want wow crew
                names on the home screen" (which referred to a different
                surface). Now back where it belongs. */}
            <h2
              className={`text-3xl sm:text-4xl font-black tracking-tight leading-tight mt-0.5 truncate ${may4Active ? "may4-glitch-intro" : ""}`}
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "var(--fb-blue)" }}
              data-testid="welcome-heading"
            >
              {user?.first_name || ""}
            </h2>
            <p className="text-neutral-500 text-sm mt-1 hidden sm:block">Let&apos;s go &mdash; what&apos;s the move?</p>
          </div>

          {/* Live ticker removed 2026-05-28 per user ask "we need to
              remove the ticking pill on the home page please". The
              <LiveTicker /> component remains in the codebase in case
              we want to re-introduce it later, but the desktop spacer
              now stays empty so the greeting + icon cluster breathe. */}
          <div className="hidden lg:flex flex-1 min-w-0 mx-6" />

          <div
            className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0 bg-white rounded-full p-1 shadow-sm"
            style={{ border: "1px solid rgba(148,163,184,0.18)" }}
          >
            {/* Inbox — for managers this lives in the header (not as a tile) so
                the manager home screen stays focused on ops. Crew still see it
                as a tile below. */}
            {isManager && (theme.crew_inbox_enabled !== false) && (
              <button
                onClick={() => onPick("inbox")}
                className="relative w-9 h-9 rounded-full hover:bg-emerald-50 transition-colors flex items-center justify-center"
                data-testid="header-inbox-button"
                aria-label={inboxUnread > 0 ? `${inboxUnread} unread messages` : "Inbox"}
                title="Inbox"
              >
                <EnvelopeSimple weight="bold" className="w-4 h-4 text-emerald-600" />
                {inboxUnread > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 text-[9px] font-black text-white min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center shadow tabular-nums"
                    style={{ backgroundColor: "#059669" }}
                    data-testid="header-inbox-badge"
                  >
                    {inboxUnread > 99 ? "99+" : inboxUnread}
                  </span>
                )}
              </button>
            )}
            {/* Coupon — small % pill in the header that pops the active
                coupon barcode in a bottom sheet. Sits at the start of
                the cluster so it reads as a "perk" rather than a
                navigation. (User clarification 2026-02-14: coupon
                belongs on the icons bar, not as a tile.) */}
            <CouponHeaderPill />
            {/* Notification cluster order (2026-05-21): communications
                grouped left (Inbox → Bell), system controls right (Sound
                → Admin → Logout), then the Profile avatar furthest right
                as the "you" anchor. Old order interleaved Sound between
                Inbox and Bell which broke the communications grouping. */}
            <button
              onClick={() => setShowNotifications(true)}
              className={`relative w-9 h-9 rounded-full transition-all flex items-center justify-center ${unreadCount > 0 ? "bg-blue-100 ring-2 ring-blue-400 animate-pulse" : "hover:bg-blue-50"}`}
              data-testid="notifications-button"
              aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
            >
              <Bell
                weight={unreadCount > 0 ? "fill" : "bold"}
                className="w-4 h-4"
                style={{ color: unreadCount > 0 ? "var(--fb-blue)" : "#64748B" }}
              />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-[9px] font-black text-white min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center shadow"
                  style={{ backgroundColor: "var(--fb-blue)" }}
                  data-testid="notifications-badge"
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={toggleCueMuted}
              className="w-9 h-9 rounded-full hover:bg-slate-100 transition-colors flex items-center justify-center"
              data-testid="cue-toggle"
              aria-label={cueMuted ? "Unmute notification sound" : "Mute notification sound"}
              title={cueMuted ? "Notifications muted — tap to unmute" : "Sound on — tap to mute"}
            >
              {cueMuted
                ? <SpeakerSlash weight="bold" className="w-4 h-4 text-slate-400" />
                : <SpeakerHigh weight="bold" className="w-4 h-4 text-slate-600" />}
            </button>
            {/* Report an issue — opens a quick modal that sends a bug /
                feature / question to all admins via email + in-app inbox.
                Visible to everyone, since any crew should be able to flag
                a broken flow. (User ask 2026-05-21.) */}
            <button
              onClick={() => setShowReportIssue(true)}
              className="w-9 h-9 rounded-full hover:bg-indigo-50 transition-colors flex items-center justify-center"
              data-testid="header-report-issue"
              aria-label="Report an issue"
              title="Report an issue · bug · feature · question"
            >
              <ChatTeardropDots weight="bold" className="w-4 h-4 text-indigo-500" />
            </button>
            {/* Admin shortcut moved 2026-05-23 — was a small shield icon
                in this header cluster. Now lives inside the profile
                avatar dropdown alongside Settings so the admin's home
                screen stays calm and the tile grid doesn't overflow on
                mobile. (User ask: "move admin and settings tiles to the
                drop down on admin profile picture".) */}
            {/* Standalone sign-out icon retired 2026-05-25 — Sign out now
                lives at the bottom of the slide-out profile panel, paired
                with a goodbye splash. Less clutter in the header cluster. */}
            <ProfileMenuButton
              user={user}
              myPhoto={myPhoto}
              storiesEnabled={theme.stories_enabled !== false && canPostStory}
              onEditProfile={() => onPick && onPick("profile")}
              onPostStory={() => setStoryComposerOpen(true)}
              onSettings={() => onPick && onPick("settings")}
              onAdmin={() => onPick && onPick("admin")}
              onOpenReports={(isManager || isDM) ? ((opts) => onPick && onPick("reports", opts)) : undefined}
              onSignOut={onSwitchUser || undefined}
              isAdmin={!!(user?.is_admin || user?._real_is_admin)}
            />
          </div>
        </div>
      </div>

      {/* 🚀 LAUNCH DAY hero (2026-05-26) — fires confetti once per
          device on first paint and renders a celebratory ribbon for the
          full day. Plain date-string compare in MST so it auto-vanishes
          at midnight MST without any admin action. Edit LAUNCH_DAY_MST
          to keep the celebration running multi-day if needed. */}
      <LaunchDayHero firstName={user?.first_name || ""} />

      {/* Celebration banner — birthdays & work anniversaries today (unified Banner). */}
      {(celebrations.birthdays.length > 0 || celebrations.anniversaries.length > 0) && (
        <div className="px-5 pt-1 flex-shrink-0 max-w-lg lg:max-w-6xl w-full mx-auto" data-testid="celebration-banner">
          <Banner
            variant="celebration"
            icon={celebrations.birthdays.length > 0 ? Cake : Trophy}
            title={
              celebrations.birthdays.length > 0
                ? (celebrations.birthdays.length === 1
                    ? `Happy Birthday, ${celebrations.birthdays[0].first_name}!`
                    : `Birthdays today: ${celebrations.birthdays.map((b) => b.first_name).join(", ")}`)
                : `${celebrations.anniversaries[0].first_name} · ${celebrations.anniversaries[0].years} ${celebrations.anniversaries[0].years === 1 ? "year" : "years"} at Five Below!`
            }
            leading={
              <div className="flex -space-x-2">
                {[...celebrations.birthdays, ...celebrations.anniversaries].slice(0, 3).map((p, i) => (
                  p.photo_url
                    ? <img key={`celeb-${i}`} src={p.photo_url} alt={p.first_name} className="w-7 h-7 rounded-full object-cover ring-2 ring-white" />
                    : <div key={`celeb-${i}`} className="w-7 h-7 rounded-full ring-2 ring-white flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: "#DB2777" }}>{(p.first_name || "?")[0].toUpperCase()}</div>
                ))}
              </div>
            }
          >
            {celebrations.birthdays.length > 0 && celebrations.anniversaries.length > 0 && (
              <p>Also today: {celebrations.anniversaries.map((a) => `${a.first_name} (${a.years} yr)`).join(", ")}</p>
            )}
            {celebrations.birthdays.length === 0 && celebrations.anniversaries.length > 1 && (
              <p>Also: {celebrations.anniversaries.slice(1).map((a) => `${a.first_name} (${a.years} yr)`).join(", ")}</p>
            )}
          </Banner>
        </div>
      )}

      {/* Stories — Instagram-style 24h ephemeral photo/video posts. Collapses
          to a subtle "Share a story" pill when nobody has posted; hidden
          entirely when admin toggles the feature off in Settings. */}
      {/* Stories — 3094-exclusive feature. We don't want Wheat Ridge stories
          (the original campaign content) leaking to crew at sister stores,
          and the feature itself hasn't been adapted for cross-store posting
          yet, so the entire row is hidden for non-3094 home stores AND
          when the admin is mirroring a sister store. (User report 2026-05-19:
          "right now when another store logs in they can see the stories
          from wheat ridge — they shouldn't even see this on their banner".)

          Updated 2026-05-21: tiered access — 3094 crew still see stories
          (their original culture feed). Sister stores: ONLY the SM + the
          DM. Sister-store regular crew see nothing. Matches the backend
          gating in routes/stories.py. */}
      {/* Instants feature removed 2026-06-15. */}

      {user?.crew_id && canUseStories && (
        <div className="px-5 flex-shrink-0 max-w-lg lg:max-w-6xl w-full mx-auto">
          <StoriesRow
            user={user}
            isManager={isManager}
            enabled={theme.stories_enabled !== false}
            composerOpen={storyComposerOpen}
            onComposerOpenChange={setStoryComposerOpen}
          />
        </div>
      )}

      {/* May the 4th hero — keyed to the active theme rather than the
          calendar so the admin has full control: it only shows when the
          "May the 4th" preset is active (detected via its distinctive
          deep-space background) and vanishes the moment the admin flips
          to any other theme. (user feedback 2026-05-04 "when we turn off
          that theme it should remove the banner"). */}
      {((theme?.background_color || "").toUpperCase() === "#050418") && (
        <div className="px-5 flex-shrink-0 max-w-lg lg:max-w-6xl w-full mx-auto" data-testid="may4-banner">
            <div
              className="relative overflow-hidden rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg may4-banner-jitter may4-hologram"
              style={{
                background: "radial-gradient(ellipse at top, #1E3A8A 0%, #050418 55%, #000000 100%)",
                border: "1px solid rgba(236, 72, 153, 0.3)",
              }}
            >
              {/* Sparkle stars — pure CSS, no asset weight */}
              <div className="absolute inset-0 pointer-events-none opacity-70" aria-hidden>
                <span className="absolute top-2 left-6 w-0.5 h-0.5 rounded-full bg-white animate-pulse" />
                <span className="absolute top-5 left-20 w-1 h-1 rounded-full bg-pink-400 animate-pulse" style={{ animationDelay: "0.4s" }} />
                <span className="absolute top-3 right-12 w-0.5 h-0.5 rounded-full bg-cyan-300 animate-pulse" style={{ animationDelay: "0.8s" }} />
                <span className="absolute bottom-3 right-24 w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "1.1s" }} />
              </div>
              {/* Lightsaber stripe — ignites on banner mount, then idle pulses. */}
              <div className="relative flex-shrink-0 w-1 h-10 rounded-full may4-saber" style={{ background: "linear-gradient(180deg, #EC4899 0%, #8B5CF6 100%)" }} aria-hidden />
              <div className="relative flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300/80">A long time ago in a store far, far away…</p>
                <p className="text-base sm:text-lg font-black text-white leading-tight truncate may4-glitch" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", textShadow: "0 0 16px rgba(236, 72, 153, 0.5)" }}>
                  May the 4th be with you!
                </p>
                <p className="text-xs text-white/80 font-semibold">Let go & have fun — the Force is strong with this WOW Crew.</p>
              </div>
              <span className="relative text-2xl flex-shrink-0" aria-hidden>🪐</span>
            </div>
          </div>
      )}

      {/* Message banner retired 2026-02-27 (user ask: "since push
          notifications now work, is it safe to remove the blast?").
          Team-wide messages still land in the Whole Team inbox thread
          and everyone gets a native push — the on-home banner was a
          workaround for when push was flaky. Component
          `CrewMessageBanner` is now dead code but left in the file
          tree in case we want to revive it seasonally. */}
      {/* Home top summary cards (2026-XX): replaces the previous
          full-width yellow "Your Tasks Today" bar + dashed Daily
          Passcode bar with two dashboard-style square cards. Tap
          opens the corresponding controlled Sheet on the invisible
          instances mounted immediately below — Home never unmounts,
          so users pop back to the exact same scroll/tile state when
          the Sheet closes. See HomeTopCards.js for the visual, and
          the `hideSummary` / `hideBar` + `open` / `onOpenChange`
          props on YourTasksTodayCard / DailyPasscodeCard for the
          controlled-Sheet plumbing. */}
      {user?.crew_id && (
        <div className="px-5 flex-shrink-0 max-w-lg lg:max-w-4xl w-full mx-auto">
          <HomeTopCards
            taskSummary={tasksSummary}
            passcodeReady={passcodeReady}
            onOpenTasks={() => setTasksSheetOpen(true)}
            onOpenPasscode={() => setPasscodeSheetOpen(true)}
          />
          {/* Invisible controllers — mount the existing components in
              headless mode so their internal Sheets / Scan flows /
              Dialogs work exactly as before, with no logic rebuild. */}
          <YourTasksTodayCard
            crewId={user.crew_id}
            storeId={user.home_store_id}
            onOpen={() => onPick && onPick("zone-sheet")}
            hideSummary
            open={tasksSheetOpen}
            onOpenChange={setTasksSheetOpen}
          />
          <DailyPasscodeCard
            user={user}
            hideBar
            open={passcodeSheetOpen}
            onOpenChange={setPasscodeSheetOpen}
          />
        </div>
      )}

      {/* Section caption for the tile grid. Redesigned 2026-08-06:
          removed the gradient rule and tightened to a single small
          uppercase caption in slate-400. The extra whitespace above
          the tile grid does the visual separation the rule used to
          do — Apple-clean pass. */}
      <div
        className="px-5 mt-5 sm:mt-6 mb-2 flex-shrink-0 max-w-lg lg:max-w-5xl w-full mx-auto flex items-center justify-between"
        data-testid="home-tiles-divider"
      >
        <span
          className="text-[10px] font-black tracking-[0.22em] text-slate-400 uppercase"
          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
        >
          Quick Actions
        </span>
        {/* Preview trigger — visible to admin only. Opens the tap-through
            HomePageV2 mockup for design feedback (2026-08-06). Remove
            when the redesign lands. */}
        {onOpenV2Preview && (user?.is_admin || user?.is_district_manager) && (
          <button
            onClick={onOpenV2Preview}
            className="text-[10px] font-black tracking-widest uppercase text-indigo-500 hover:text-indigo-700 inline-flex items-center gap-1"
            data-testid="home-open-v2-preview"
          >
            <span aria-hidden>\u2728</span> Preview new home
          </button>
        )}
      </div>

      {/* Tile list — modern app-icon grid. Mobile: 2 columns so tiles read
          as buttons (not list rows). Desktop: 3 columns max with a center-
          aligned max-width. (Redesign 2026-02-22: pastel rainbow → unified
          FB brand palette, 2-col grid.) */}
      <div className={`px-4 sm:px-5 max-w-lg lg:max-w-5xl w-full mx-auto pt-1 pb-6 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-3 lg:gap-4 ${may4Active ? "" : "fb-tiles-bounce"}`}>
        {/* Unified tile order (2026-05-21 user spec): everyone sees the
            same sequence below, visibility flags still hide irrelevant
            tiles per role. Order: Collect Emails → Email Contest → Wow
            Crew → What's Up → District → Manager Dashboard → Settings
            → Admin. Inbox (crew-only) + Reports (manager-only) are
            tucked into adjacent slots so they don't disrupt the spec. */}

        {/* 1. Collect Emails — primary action, FB blue. Hidden for District
            Managers: they're not on the sales floor capturing emails; their
            mission is district-wide oversight via the District tile.
            Spider-Man theme easter egg (2026-02): triple-tap the tile
            within 350ms fires "spider:thwip" (whoosh + web confetti)
            instead of navigating. Single tap still navigates
            normally after a short debounce so the delay is
            imperceptible during normal use. */}
        {!isDM && !isTileHidden("collect-emails") && (
          <ChoiceTile
            icon={Envelope}
            title="Collect Emails"
            subtitle="Tap +1 every capture"
            theme="blue"
            onClick={emailTileTap}
            testid="choice-collect-emails"
            delay={0}
            liveCount={todayCount}
            personality="envelope"
            alive={todayCount > 0}
          />
        )}

        {/* Penny Rounding tile removed 2026-10-04 — feature retired. */}

        {/* 3. Zone Sheet — Phase 2 of Manager Dashboard reshape. Sits at
            position 2 because the user runs the floor off this sheet
            every shift (user ask 2026-02-14). Visible to everyone at
            the store; crew see what they've been assigned to, managers
            edit. Amber theme reads as "operational ops" alongside the
            navy Manager Dashboard. */}
        {!isTileHidden("zone-sheet") && (
        <ChoiceTile
          icon={ClipboardText}
          title="Zone Sheet"
          subtitle="Tasks · Eyes on the Biz · Crew"
          theme="amber"
          onClick={() => onPick("zone-sheet")}
          testid="choice-zone-sheet"
          delay={20}
          personality="clipboard"
          alive={(tasksSummary.done + tasksSummary.inProgress) > 0}
        />
        )}

        {/* 3. Manager Dashboard (store managers only, not DM) — promoted
            to position #3 on 2026-06-13 per user spec "Manager
            Dashboard is becoming a key daily command center". For
            crew this slot is skipped; the next tile (Email Contest)
            slides up into the spot. Theme stays navy; route/title/
            testid all unchanged so existing deep-links + bell
            notifications still land here. WTD financials, task
            oversight, EOD recap. Checkpoint readings moved to Zone
            Sheet → Eyes on the Biz 2026-06-12; subtitle updated
            2026-06-13 to remove the stale "checkpoints" mention. */}
        {isManager && !isDM && !isTileHidden("manager-dashboard") && (
          <ChoiceTile
            icon={ChartLineUp}
            title="Manager Dashboard"
            subtitle="Win The Day · Mission Control"
            theme="navy"
            onClick={() => onPick("manager")}
            testid="choice-manager-dashboard"
            delay={35}
            alert={dashboardAlert}
            liveCount={managerCaptureRate}
            liveUnit="%"
            liveLabel="capture"
            liveLabelZero="capture"
            personality="chart"
            alive={managerCaptureRate !== null && managerCaptureRate > 0}
          />
        )}

        {/* 4. Email Contest — gold yellow. Hidden for the DM (her contest
            view lives inside the District Dashboard as a roll-up across all
            stores, not as a single-store ticket tracker). Also hidden when
            admins flip `tile_visible` off in the Contest settings page —
            that's the contest-is-over state so the tile doesn't clutter
            the home grid with stale "Ended" copy. (User ask 2026-06-04.)

            Bug fix 2026-06-15: previously gated on
            `contestSummary?.tile_visible !== false`, which is TRUE when
            contestSummary is still null on first mount → the tile flashed
            IN before the server response and then disappeared. Users
            reported "tile keeps coming back." Now we render ONLY when
            contestSummary is loaded AND tile_visible is explicitly true
            (default-hidden). Kills the flash and prevents the briefly-
            visible-but-not-clickable state. */}
        {!isDM && contestSummary?.tile_visible === true && (
          <ChoiceTile
            icon={Trophy}
            title="Email Contest"
            subtitle={
              contestSummary
                ? contestSummary.is_active
                  ? `$${contestSummary.prize_per_crew} prize · ${contestSummary.days_left}d left`
                  : contestSummary.days_left > 0
                    ? `Starts in ${contestSummary.days_left}d · $${contestSummary.prize_per_crew} prize`
                    : `Ended · ${contestSummary.locked_tickets}/${contestSummary.max_possible} tickets`
                : "Earn tickets every week"
            }
            theme="yellow"
            onClick={() => onPick("contest")}
            testid="choice-contest"
            delay={45}
            liveCount={contestSummary ? contestSummary.total_tickets : null}
            liveLabel={contestSummary && contestSummary.total_tickets === 1 ? "ticket" : "tickets"}
            liveLabelZero="tickets"
            alert={contestAlert}
            personality="trophy"
            alive={contestSummary?.total_tickets > 0}
          />
        )}

        {/* 3. Wow Crew — teal. Hidden for DM since their team view is the
            cross-store leaderboard inside the District Dashboard. */}
        {!isDM && !isTileHidden("team") && (
          <ChoiceTile
            icon={UsersThree}
            title="Wow Crew"
            subtitle="Roster · profiles · celebrations"
            theme="teal"
            onClick={() => onPick("team")}
            testid="choice-team"
            delay={90}
            personality="people"
            alive={true}
          />
        )}

        {/* 3b. Leaderboards — promoted from a Wow Crew sub-tab to its own
            top-level tile so the entire team sees standings the moment they
            open the app (user ask 2026-05-22). Visible to everyone — crew
            see their own store first; DM/admin/managers get the district
            sub-tabs alongside. */}
        {!isTileHidden("leaderboard") && (
        <ChoiceTile
          icon={Trophy}
          title="Leaderboards"
          subtitle="Your store · district stores · district crew"
          theme="pink"
          onClick={() => onPick("leaderboard")}
          testid="choice-leaderboard"
          delay={105}
          personality="podium"
          alive={true}
        />
        )}

        {/* Inbox (crew-only tile) — managers have it in the header. Tucked
            here right after Wow Crew so the communications cluster reads
            naturally without breaking the spec'd 8-tile order. */}
        {!isManager && theme.crew_inbox_enabled !== false && (
          <ChoiceTile
            icon={EnvelopeSimple}
            title="Inbox"
            subtitle="DMs, blasts & group chats"
            theme="navy"
            onClick={() => onPick("inbox")}
            testid="choice-inbox"
            badgeCount={inboxUnread}
            delay={120}
          />
        )}

        {/* 4. What's Up — calendar. Everyone sees this. */}
        {!isTileHidden("calendar") && (
        <ChoiceTile
          icon={CalendarBlank}
          title="What's Up"
          subtitle="Events & celebrations"
          theme="violet"
          onClick={() => onPick("calendar")}
          testid="choice-calendar"
          delay={140}
          personality="calendar"
          alive={true}
        />
        )}

        {/* 6. District (DM + admin + Training SM) — cross-store roll-up.
            For the DM this is the primary entry point; for admin it's
            the "View as DM" shortcut; for a Training SM it's read-only
            shadow access (subtitle hints at the limit). */}
        {(isDM || isAdmin || isTSM) && (
          <ChoiceTile
            icon={Buildings}
            title="District"
            subtitle={isDM ? "11 stores · your district" : isTSM ? "11 stores · shadow view" : "11 stores · cross-store view"}
            theme="orange"
            onClick={() => onPick("district")}
            testid="choice-district"
            delay={180}
            personality="buildings"
            alive={true}
          />
        )}

        {/* Manager Dashboard tile moved to position #3 above (user
            ask 2026-06-13). The previous slot here intentionally
            left empty so the surrounding spec'd order stays
            readable. */}

        {/* Reports tile removed from Home 2026-02 — moved to the Profile
            slide-out menu next to Admin / Settings. Reduces home grid
            clutter for the majority (crew) who never open Reports and
            surfaces a small red-dot indicator when a fresh weekly
            rollup is available so managers still get a nudge. */}

        {/* Settings tile retired 2026-05-23 — moved to the profile avatar
            dropdown alongside the new Admin entry so the home grid stops
            feeling cluttered on the admin screen. Crew at sister stores
            still reach Settings via the same dropdown. */}

        {/* Admin tile retired 2026-05-21 — moved to a small shield icon in
            the header cluster so the home grid doesn't overflow on mobile.
            Then relocated again 2026-05-23 to the profile avatar dropdown. */}
      </div>

      {/* Install app prompt footer (sign-out moved to header next to the bell).
          Last-login hint sits just below so crew see when they last signed in
          from the same device — handy signal on shared iPads. Padding is
          tighter now that the Admin tile no longer occupies the last row. */}
      {/* Penny Rounding sheet removed 2026-10-04 — feature retired. */}
      <div className="px-5 pb-6 pt-2 flex-shrink-0 max-w-lg lg:max-w-6xl w-full mx-auto flex flex-col items-center gap-1">
        <InstallAppPrompt variant="footer" />
        {formatLastLogin(user?.previous_login) && (
          <p
            className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400"
            data-testid="home-last-login"
          >
            {formatLastLogin(user?.previous_login)}
          </p>
        )}
        {/* Founder credit — tap "Dom" to open his Report Card from any
            session (works for sister-store crew too via the
            /credits/founder lookup). (User ask 2026-05-25.) */}
        <CreditByDom variant="dark" />
        {/* Version badge moved to the notifications-bell pop-out so it's
            always reachable without scrolling. (user feedback 2026-05-04
            "we can't see the version at the bottom of the app, can we add
            that to the notifications bell pop out".) */}
      </div>

      {showNotifications && (
        <NotificationsPanel
          notifications={unreadNotifications}
          appVersion={pkg.version}
          currentUser={user}
          onRefresh={fetchNotifications}
          onClose={() => setShowNotifications(false)}
          onMarkAllRead={handleMarkAllRead}
          onRowClick={(n) => routeNotification(n, {
            nav: (route, meta) => onPick && onPick(route, meta),
            openSheet: (k) => onPick && onPick(k),
            openNotifications: () => setShowNotifications(true),
            setEodReport,
            crewId: user?.crew_id || "",
            closePanel: () => setShowNotifications(false),
          })}
          data-legacy-router-retired="2026-10-04"
          onRowClickRetired={(n) => {
            // Route based on the notification type. DMs / mentions /
            // reactions live in the Inbox; missed-checkpoint reminders go
            // to the Manager Dashboard; the new EOD ready notif opens the
            // on-demand report modal in-place (no navigation). Bug-report
            // notifications route to the admin triage page (admin side)
            // or the reporter's Inbox thread (reporter side). (User ask
            // 2026-05-23 "in the notification panel can we make tickets
            // clickable".)
            setShowNotifications(false);
            if (n.type === "eod_ready") {
              setEodReport({ date: n.target_date, store_id: n.store_id });
              return;
            }
            if (n.type === "coverage_completion" || n.type === "coverage_handoff") {
              // Deep-link the manager into the Live Feed with the
              // specific task pre-selected. The dashboard reads
              // `wow:openCoverageTask` from sessionStorage on mount,
              // opens the inline detail modal, then clears the flag.
              try {
                sessionStorage.setItem(
                  "wow:openCoverageTask",
                  JSON.stringify({ task_id: n.task_id, date: n.date, store_id: n.store_id }),
                );
              } catch { /* quota */ }
              onPick && onPick("manager");
              return;
            }
            if (n.type === "coverage_help_request") {
              // Batch C — Peer Help (2026-06-15). Tap → confirm dialog
              // → POST accept_help. The notification carries
              // `task_id` as "coverage:<uuid>" and `store_id`. We need
              // the row owner's crew_id to call the transition — look
              // it up from the task message? Easier: re-fetch the
              // store's coverage summary and find the matching task.
              const taskUuid = String(n.task_id || "").replace(/^coverage:/, "");
              // eslint-disable-next-line no-alert
              if (!window.confirm(`${n.message}\n\nTap OK to let them know you're jumping in to help.`)) return;
              (async () => {
                try {
                  // Pull the summary to find this task's owner crew_id.
                  const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/zone-sheets/${n.date}/coverage-tasks/summary`, {
                    params: { actor_id: user.crew_id, store_id: n.store_id || "" },
                  });
                  const items = (r.data && r.data.items) || [];
                  const row = items.find((it) => it.task_id === taskUuid);
                  if (!row) {
                    toast.error("Task no longer exists.");
                    return;
                  }
                  await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/zone-sheets/${n.date}/coverage-tasks/transition`,
                    { actor_id: user.crew_id, crew_id: row.crew_id, task_id: taskUuid, transition: "accept_help" },
                    { params: { store_id: n.store_id || "" } },
                  );
                  toast.success(`You're helping ${row.crew_name || "your teammate"} with ${row.label}.`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't accept help request.");
                }
              })();
              return;
            }
            if (n.type === "coverage_help_accepted") {
              // Owner-side bell — a teammate jumped in. Surface the
              // home screen so they see the "teammate helping" pill on
              // YourTasksTodayCard.
              onPick && onPick("home");
              return;
            }
            if (n.type === "coverage_nudge") {
              // Soft nudge bell — route to home so the YourTasksTodayCard
              // is visible. The card auto-opens to the in-progress task list.
              onPick && onPick("home");
              return;
            }
            if (n.type === "checkpoint_reminder" || n.type === "eod_missing") {
              // Deep-link to the Zone Sheet's Eyes on the Biz entry
              // form for the missing slot. Slot is stamped on the
              // notification doc (server.py / background_scheduler.py).
              // ZoneSheetPage reads `wow:openCheckpoint` from
              // sessionStorage on mount, scrolls the Eyes on the Biz
              // tile into view, expands it, and fires the existing
              // `wow:openCheckpoint` CustomEvent so CheckpointEntry
              // switches to the right tab. (User report 2026-06-12
              // "the overdue reading banner / bell notification
              // doesn't route anywhere — should jump to the Eyes on
              // the Biz section and focus the correct time slot".
              // Final/8PM safety-net was added 2026-06-13 — its doc
              // type is `eod_missing` but routes identically.)
              const slotFromId = (() => {
                if (n.slot) return n.slot;
                const parts = String(n.id || "").split("_");
                const idx = parts.indexOf("reminder");
                return idx >= 0 && parts[idx + 2] ? parts[idx + 2] : "";
              })();
              try {
                sessionStorage.setItem(
                  "wow:openCheckpoint",
                  JSON.stringify({ slot: slotFromId, date: n.date || "", store_id: n.store_id || "" }),
                );
              } catch { /* quota — ignore */ }
              onPick && onPick("zone-sheet");
              return;
            }
            else if (n.type === "bug_report") onPick && onPick("admin", { initialView: "bug_reports" });
            else if (n.type === "bug_report_self" || n.type === "bug_report_status") onPick && onPick("inbox");
            else if (n.type === "store_closure") onPick && onPick("settings");
            else if (n.type === "story_mention" || n.type === "story_view" || n.type === "story_reaction") onPick && onPick("home");
            else onPick && onPick("inbox");
          }}
        />
      )}
      {eodReport && (
        <EODReportModal
          date={eodReport.date}
          storeId={eodReport.store_id}
          user={user}
          onClose={() => setEodReport(null)}
        />
      )}
      {/* Milestone celebration — full-screen confetti + chime when the
          store hits goal or wins a contest ticket. Auto-dismisses; tap
          anywhere to close early. (Desktop-fun pack, 2026-05-21.) */}
      <MilestoneCelebration milestone={milestone} onDone={() => setMilestone(null)} />

      {/* Report-issue modal — mounted at the root so any header tap or
          Settings-footer tap can open it without prop drilling. */}
      {showReportIssue && (
        <ReportIssueModal
          user={user}
          onClose={() => setShowReportIssue(false)}
          currentPage="home"
        />
      )}
    </div>
  );
}
